import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, UserCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const FollowButton = ({ targetUserId, targetHandle, size = 'default', variant = 'default' }) => {
  const { user } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (user && targetUserId && targetUserId !== user?.id) {
      checkFollowStatus();
    } else {
      setChecking(false);
    }
  }, [targetUserId, user]);

  const checkFollowStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/${targetUserId}/follow-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIsFollowing(response.data.following);
      setChecking(false);
    } catch (error) {
      console.error('Error checking follow status:', error);
      setChecking(false);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error('Please sign in to follow creators');
      return;
    }

    if (targetUserId === user?.id) {
      toast.error("You can't follow yourself");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/users/${targetUserId}/follow`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setIsFollowing(response.data.following);
      
      if (response.data.following) {
        toast.success(`Following @${targetHandle || 'user'}!`);
      } else {
        toast.success(`Unfollowed @${targetHandle || 'user'}`);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to follow user');
      setLoading(false);
    }
  };

  // Don't show button for own profile
  if (targetUserId === user?.id) {
    return null;
  }

  // Don't show if not authenticated
  if (!user) {
    return null;
  }

  if (checking) {
    return (
      <Button disabled size={size} variant="outline" className="border-gray-800">
        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
      </Button>
    );
  }

  return (
    <Button
      onClick={handleFollow}
      disabled={loading}
      size={size}
      variant={variant}
      className={
        isFollowing
          ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white'
          : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
      }
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      ) : isFollowing ? (
        <>
          <UserCheck className="w-4 h-4 mr-2" />
          Following
        </>
      ) : (
        <>
          <UserPlus className="w-4 h-4 mr-2" />
          Follow
        </>
      )}
    </Button>
  );
};

export default FollowButton;
